import { useQuery } from '@tanstack/react-query';
import { PassportSectionFallback } from './PassportSectionFallback';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ClipboardCheck, Clock, TrendingUp } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { subDays, format } from 'date-fns';
// DTC-K: route the passport-inspection-metrics "now" anchor through
// the canonical clock so passport-sync analytics windows are
// deterministic under `clock.setFixed(...)`.
import { clock } from '@/utils/clock';

interface Props {
  technicianProfileId: string;
}

interface PeriodStats {
  label: string;
  count: number;
  bookHours: number;
  actualHours: number;
  efficiency: number;
}

export function PassportInspectionMetrics({ technicianProfileId }: Props) {
  const { data: stats, isLoading, isError } = useQuery({
    queryKey: ['passport-inspection-metrics', technicianProfileId],
    queryFn: async (): Promise<PeriodStats[]> => {
      // Resolve technician_profile_id -> user_id via technicians + technician_users
      // CORE-C GAP-003: an error on either resolution read is not the same
      // answer as "this person has no technician row". The latter is a real
      // absence and still returns []; the former must reach the component as
      // a failure.
      const { data: techRow, error: techRowError } = await supabase
        .from('technicians')
        .select('id')
        .eq('technician_profile_id', technicianProfileId)
        .limit(1)
        .maybeSingle();

      if (techRowError) throw techRowError;
      if (!techRow) return [];

      const { data: tuRow, error: tuRowError } = await supabase
        .from('technician_users')
        .select('user_id')
        .eq('technician_id', techRow.id)
        .limit(1)
        .maybeSingle();

      if (tuRowError) throw tuRowError;
      if (!tuRow?.user_id) return [];

      const now = clock.now();
      const periods = [
        { label: '30 Days', start: format(subDays(now, 30), 'yyyy-MM-dd') },
        { label: '90 Days', start: format(subDays(now, 90), 'yyyy-MM-dd') },
      ];

      const results: PeriodStats[] = [];

      for (const period of periods) {
        const { data, error } = await supabase
          .from('ticket_workflow_inspections' as any)
          .select('book_time_min, total_duration_seconds')
          .eq('performed_by', tuRow.user_id)
          .not('completed_at', 'is', null)
          .gte('completed_at', period.start);

        // CORE-C GAP-003: this used to log the error and push a row of ZEROS —
        // 0 inspections, 0 hours, 0% efficiency — which then either rendered
        // beside a real period as though it had been measured, or, when every
        // period failed, made `stats.every(s => s.count === 0)` true and the
        // whole section silently disappear. A period that could not be read is
        // not a period in which the person did nothing.
        if (error) throw error;

        const rows = (data as any[]) || [];
        const count = rows.length;
        const bookMinutes = rows.reduce((sum: number, r: any) => sum + (r.book_time_min || 0), 0);
        const actualSeconds = rows.reduce((sum: number, r: any) => sum + (r.total_duration_seconds || 0), 0);
        const bookHours = bookMinutes / 60;
        const actualHours = actualSeconds / 3600;
        // KPI-R72-FIX (HIGH-6): standardize on `book / actual` efficiency
        // (values >100% = faster than book). Previously used the inverted
        // `actual / book`, which showed a tech at e.g. 110% when they were
        // actually 10% SLOWER than book — the opposite meaning from every
        // other efficiency stat on the passport. The color bands below
        // were also written for the inverted sense; flipped to match the
        // standard convention.
        const efficiency = actualHours > 0 ? bookHours / actualHours : 0;

        results.push({ label: period.label, count, bookHours, actualHours, efficiency });
      }

      return results;
    },
    enabled: !!technicianProfileId,
    staleTime: 5 * 60 * 1000,
  });

  // ── Order matters: APPLICABILITY, then retrieval state ──────────────────
  //
  // Without a technician profile this section does not apply to this Passport
  // at all, and that decision is made without reference to any read. A failed
  // retrieval must never be the thing that reveals a section the viewer was
  // not going to be shown.
  if (!technicianProfileId) return null;

  // Applicable. A read that did not happen is now distinguishable from a
  // person who performed no inspections.
  if (isError) {
    return <PassportSectionFallback sectionName="Inspection Metrics" />;
  }

  // Genuine absence keeps its existing behaviour: the section disappears.
  if (isLoading || !stats || stats.every(s => s.count === 0)) return null;

  // KPI-R72-FIX (HIGH-6): color bands aligned to the standard efficiency
  // convention (book / actual): ≥1.0 → green (on/under book), 0.8–1.0 →
  // yellow, <0.8 → red. Mirrors InspectionPerformanceCard + TechnicianPerformanceCard.
  const effColor = (eff: number) =>
    eff >= 1 ? 'text-green-600' : eff >= 0.8 ? 'text-yellow-600' : 'text-red-600';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <ClipboardCheck className="h-5 w-5" />
          Inspection Performance
        </CardTitle>
        <CardDescription>Workflow inspection book vs actual time</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {stats.map((period) => (
            <div key={period.label} className="p-4 bg-muted/30 rounded-lg space-y-3">
              <div className="font-semibold text-sm">{period.label}</div>
              <div className="grid grid-cols-2 gap-3 text-center">
                <div>
                  <div className="text-xl font-bold">{period.count}</div>
                  <div className="text-xs text-muted-foreground">Inspections</div>
                </div>
                <div>
                  <div className={`text-xl font-bold ${effColor(period.efficiency)}`}>
                    {(period.efficiency * 100).toFixed(0)}%
                  </div>
                  <div className="text-xs text-muted-foreground">Efficiency</div>
                </div>
                <div>
                  <div className="text-sm flex items-center justify-center gap-1">
                    <Clock className="h-3 w-3" />
                    {period.bookHours.toFixed(1)}h
                  </div>
                  <div className="text-xs text-muted-foreground">Book</div>
                </div>
                <div>
                  <div className="text-sm flex items-center justify-center gap-1">
                    <Clock className="h-3 w-3" />
                    {period.actualHours.toFixed(1)}h
                  </div>
                  <div className="text-xs text-muted-foreground">Actual</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
